import React, { useEffect, useRef, useState } from 'react'
import { Box, Grid, List, Typography, CircularProgress } from '@mui/material'
import { useStyles } from '../../style/styles'
import { IProps } from './types'
import ChatMessage from '../ChatMessage'
import { checkIfBlockIndicatorMessage, deleteChannelMessage, isIndicatorMessage, listChannelMessages, removeFromScreen, splitUserArn, updateReadMarker, userArn } from '../../lib/messageUtils'
import { ChannelDetails, RootState } from '../../redux/channel/types'
import { useSelector, useDispatch } from 'react-redux'
import { MessagePayload } from '../Chat/types'
import { MessagingConstants } from '../../lib/enums'
import  Fab  from '@mui/material/Fab'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import TermsOfUse from '../TermsOfUse'
import { setChannelData } from '../../redux/channel/actions'
import { logError } from '../../lib/rumLogger'

function ChatList(props: IProps) {
  const classes = useStyles()
  const channelReducer = useSelector((state: RootState) => state.channels.channels)
  const [messageList, setMessageList] = useState<MessagePayload[] | null>(null)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [fixedChatListHeight, setFixedChatListHeight] = useState(0)
  const [displayBtn, setDisplayBtn] = useState(false);
  const [lastMessage, setLastMessage] = useState<any>({})
  const [isBlockedConvo, setIsBlockedConvo] = useState<boolean>(false)
  const [hasBlocked, setHasBlocked] = useState<boolean>(false)
  const [nextToken, setNextToken] = useState<string | null>(null)
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState<boolean>(false)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)
  const [lastMessageId, setLastMessageId] = useState<string | null>(null)

  const {
    channel,
    channelArn,
    isEndedConversation,
    senderUserArn,
    channelName,
    userType,
    onLoadMessages,
    connections,
    inputFocused,
    timezone,
    channelMetadata,
    endOrReenableActionDoer,
    receiver,
    job,
    sender,
    hasReadLastMessage,
    setHasReadLastMessage,
    initialNextToken
  } = props

  const chatListRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    /* TJSKR-2399: add checking to only trigger read marker once per new message */
    if(!hasReadLastMessage && messageList && senderUserArn ) {
      if(channelArn && channelArn !== '') {
        if(messageList && messageList.length > 0) {
          const lastMessage = messageList[messageList.length - 1]
          const isLastMessageSender = splitUserArn(lastMessage?.Sender?.Arn) == senderUserArn
          if(!isLastMessageSender) {
            const isSelf = lastMessage && isLastMessageSender ? true : false
            updateReadMarker(sender?.messaging_id, channelArn, isSelf, receiver)
            setHasReadLastMessage(true)
          }
        }
      }
    }
  }, [hasReadLastMessage, messageList, senderUserArn, channelArn])

  useEffect(() => {
    if(connections.isBlocked || connections.isBlockedLink) {
      if(connections.isBlocked) {
        setIsBlockedConvo(true)
        setHasBlocked(false)
      } else {
        setIsBlockedConvo(true)
        setHasBlocked(true)
      }
    } else {
      if(channel && channel?.ChannelSummary?.Metadata) {
        const parsedMetadata = JSON.parse(channel.ChannelSummary?.Metadata)
        if(parsedMetadata?.BlockedBy && parsedMetadata?.BlockedBy?.trim() !== '') {
          setIsBlockedConvo(true)
          setHasBlocked(false)
          window.parent.postMessage({ type: MessagingConstants.END_CONVERSATION_MARKER, jobseekerId: receiver.external_user_id, jobId: job.job_id }, '*');
          if(parsedMetadata?.BlockedBy == senderUserArn) {
            setHasBlocked(true)
          }
        } else if((parsedMetadata?.EndConversationTimestamp && parsedMetadata?.EndConversationTimestamp?.trim() !== '')) {
          window.parent.postMessage({ type: MessagingConstants.END_CONVERSATION_MARKER, jobseekerId: receiver.external_user_id, jobId: job.job_id }, '*');
        } else {
          setIsBlockedConvo(false)
          setHasBlocked(false)
          window.parent.postMessage({ type: MessagingConstants.REENABLE_CONVERSATION_MARKER, jobseekerId: receiver.external_user_id, jobId: job.job_id }, '*');
        }
      } else {
        setIsBlockedConvo(false)
        setHasBlocked(false)
      }
    }
    const channelFromReducer = channelReducer[channelName]
    if (channelFromReducer?.messageList?.length > 0 && !isLoadingMoreMessages) {
      filterMessageList()
    }
  }, [channelReducer[channelName], channel, isLoadingMoreMessages])

  // Initialize nextToken from the prop when it changes
  useEffect(() => {
    if (initialNextToken !== null) {
      setNextToken(initialNextToken)
    }
  }, [initialNextToken]);

  const handleScrollToBottom = () => {
    if (chatListRef.current) {
      chatListRef.current.scrollTop = chatListRef.current.scrollHeight
    }
  }

  const handleScroll = () => {
    if (chatListRef.current) {
      const scrollTop = chatListRef.current.scrollTop
      const scrollHeight = chatListRef.current.scrollHeight
      const clientHeight = chatListRef.current.clientHeight
      const currentScroll = scrollTop + clientHeight
      const chatListHeight = scrollHeight - clientHeight
      const isScrollingUp = scrollTop < scrollPosition;

      /* TJSKR-2744 Check if user has scrolled to the top (with a small threshold) */
      if (scrollTop <= 5 && channelArn && nextToken) {
        loadMoreMessages();
      }

      setScrollPosition(scrollTop);
      setFixedChatListHeight(chatListHeight)

      if (currentScroll <= chatListHeight) {
        setDisplayBtn(true);
      } else {
        setDisplayBtn(false);
      }
    }
  };

  const dispatch = useDispatch();
  
  /* TJSKR-2744 */
  const loadMoreMessages = async () => {
    if (channelArn && senderUserArn && nextToken) {
      try {
        setIsLoadingMoreMessages(true)
        const currentMessages = channelReducer[channelName]?.messageList || []
        const listMessages = await listChannelMessages(channelArn, senderUserArn, nextToken)
        const newMessages = listMessages?.Messages || []
        const newNextToken = listMessages?.NextToken || null
        // update nextToken for next pagination
        setNextToken(newNextToken);

        if (newMessages.length > 0) {
          if (chatListRef.current) {
            const prevScrollHeight = chatListRef.current.scrollHeight
            const existingMessageIds = new Map()
            currentMessages.forEach(msg => {
              existingMessageIds.set(msg.MessageId, true)
            })

            const uniqueNewMessages = newMessages.filter(msg => !existingMessageIds.has(msg.MessageId))

            if (uniqueNewMessages.length > 0) {
              const combinedMessages = [...uniqueNewMessages, ...currentMessages]
              const updatedChannelsReducer = { ...channelReducer }
              updatedChannelsReducer[channelName] = {
                ...updatedChannelsReducer[channelName],
                messageList: combinedMessages
              }
              dispatch(setChannelData(updatedChannelsReducer))
            }

            setTimeout(() => {
              if (chatListRef.current) {
                const newScrollHeight = chatListRef.current.scrollHeight
                const heightDifference = newScrollHeight - prevScrollHeight
                chatListRef.current.scrollTop = heightDifference > 0 ? heightDifference : 0;
              }
              setIsLoadingMoreMessages(false);
            }, 100);
          }
        } else {
          setIsLoadingMoreMessages(false);
        }
      } catch (error) {
        logError(error as Error, 'loadMoreMessages', { channelArn, channelName })
        setIsLoadingMoreMessages(false);
      }
    }
  };

  /* TJSKR-2744 Scroll to bottom when component mounts or when messageList updates (but not when loading more messages) */
  /* Temporarily reverted */

    useEffect(() => {
      const scrollToBottom = () => {
        setNextToken(initialNextToken)
        if (chatListRef.current) {
          chatListRef.current.scrollTop = chatListRef.current.scrollHeight
        }
      };

      // Only scroll to bottom if the last message has changed
      if (messageList && messageList.length > 0) {
        const currentLastMessageId = messageList[messageList.length - 1]?.MessageId;

        // Check if the last message ID has changed
        if (currentLastMessageId && currentLastMessageId !== lastMessageId) {
          setLastMessageId(currentLastMessageId);
          scrollToBottom();
          const timeoutId = setTimeout(scrollToBottom, 100);
          return () => clearTimeout(timeoutId);
        }
      }
    }, [messageList, lastMessageId, initialNextToken]);

  /* End of TJSKR-2744 */

  useEffect(() => {
    const scrollElement = chatListRef.current;
    if(scrollElement) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [scrollPosition]);

  const filterMessageList = async() => {
    // add redacted to message list
    let messageListTemp = channelReducer[channelName]?.messageList
    setLastMessage(messageListTemp[messageListTemp.length - 1])
    let filteredMessages = messageListTemp.filter(message => {
      const { Redacted, Metadata, Sender, Content } = message
      if(Redacted) {
        return false
      } else {
        if(Metadata === null) {
          return true
        } else {
            if(checkIfBlockIndicatorMessage(Content) ) {
              return false
            }
            else{
              if (JSON.parse(Metadata).removedFromScreenTimestamp === null) {
                return true
              } else {
                if( splitUserArn(Sender.Arn) == senderUserArn) {
                  return true
                } else {
                  if(isIndicatorMessage(Content)) {
                    return true
                  } else {
                    /* WIP: revisit list messages filtering */
                    return true
                  }
                }
              }
            }
          }
      }
    });
    setMessageList(filteredMessages)
  }

  const handleRemoveOrUndoMessage = async(
    channelArn: string,
    messageId: string,
    content: string,
    userId: string,
    removeType: string) => {
    let isSuccessful = null

    if(removeType == MessagingConstants.REMOVE_FROM_SCREEN) {
      isSuccessful = await removeFromScreen(
        channelArn,
        content,
        messageId,
        userId,
        MessagingConstants.REMOVED_FROM_SCREEN_TIMESTAMP
      )
    } else if(removeType == MessagingConstants.UNDO_REMOVE_MESSAGE) {
      isSuccessful = await removeFromScreen(
        channelArn,
        content,
        messageId,
        userId,
        MessagingConstants.UNDO_REMOVE_MESSAGE
      )
    } else if(removeType == MessagingConstants.REMOVE_FOR_EVERYONE) {
      isSuccessful = await deleteChannelMessage(
        channelArn,
        messageId,
        userId
      )
    } else {
      throw ('RemoveType is not valid' + removeType)
    }

    onLoadMessages()
    // remove redacted in messageList
    if (messageList) {
      setMessageList(messageList.filter((message, index) => message.MessageId !== messageId))
    }
  }

  const handleBodyClick = () => {}

  return (
    <div className={ props.platform == 'mobile' ? classes.mobileChatListContainer : classes.chatListContainer} ref={chatListRef} onClick={handleBodyClick}>
      <div className={classes.innerChatBody}>
      <div className={classes.messageWrapper}>
        {/* Loading indicator for loading more messages */}
        {isLoadingMoreMessages && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: '10px',
              position: 'sticky',
              top: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              zIndex: 1
            }}
          >
            <CircularProgress size={20} />
            <Typography variant="body2" sx={{ marginLeft: '8px', fontSize: '12px', color: '#666' }}>
              Loading more messages...
            </Typography>
          </Box>
        )}
        <List>
          {messageList && messageList.length > 0 && messageList.map((message, index) => {
            /* TJSKR-2889: Find the actual previous message (for avatar display conditions) */
            let actualPrevMessage = null;
            if (index > 0) {
              for (let i = index - 1; i >= 0; i--) {
                const potentialPrevMessage = messageList[i]
                if (new Date(potentialPrevMessage.CreatedTimestamp) < new Date(message.CreatedTimestamp)) {
                  actualPrevMessage = potentialPrevMessage
                  break
                }
              }
            }

            return (
              <ChatMessage
                key={message.MessageId}
                message={message}
                prevMessage={actualPrevMessage}
                userArn={senderUserArn}
                isEndedConversation={isEndedConversation}
                isLastMessage={index === messageList.length - 1}
                userType={userType}
                channelArn={channelArn || null}
                handleDeleteMessage={handleRemoveOrUndoMessage}
                connections={connections}
                timezone={timezone}
                channelMetadata ={channelMetadata ?? ''}
                endOrReenableActionDoer={endOrReenableActionDoer}
                lastMessage={lastMessage}
                // platform='mobile' // recheck
              />
            );
          })}
          {/* TO REVERT: SCROLL KEY */}
          {/* {displayBtn && (
            <Box
              sx={{
                display: 'flex',
                position: 'fixed',
                right: '30px',
                bottom: '70px',
                padding: '5px'
              }}>
              <Fab
                sx={{
                  backgroundColor: 'white',
                  color: '#2F86DD'
                }}
                hidden={displayBtn} size='small'
                onClick={handleScrollToBottom}>
                <KeyboardArrowDownIcon />
              </Fab>
            </Box>
          )} */}
        </List>

        { (isBlockedConvo || !sender.termsAgreed) && (
          <div className={classes.messageContentEndConversationWrapper}>
            {/* for blocked convo */}
            {isBlockedConvo && (
              <div className={classes.messageContentEndConversation}>
              <Typography className={classes.messageTextEndConversation}>
                {hasBlocked ? (
                  <>You have blocked this user.</>
                ) : (
                  <>You have been blocked by this user.</>
                )}
              </Typography>
            </div>
            )}
            
          </div>
      )}

      </div>

         
      </div>
    </div>
  )
}

export default ChatList
